import type { RoleOut, TeamOut } from "@sagot/api-client";
import { PLAN_LIMITS, type PlanKey } from "@sagot/constants";

/** Most invitations accepted in one request (backend `InvitationCreate.emails`). */
export const MAX_INVITE_EMAILS = 50;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ParsedEmails {
  valid: string[];
  invalid: string[];
}

/** Comma / semicolon / newline separated list → unique lower-cased addresses + the rejects. */
export function parseEmails(raw: string): ParsedEmails {
  const tokens = raw
    .split(/[\s,;]+/)
    .map((token) => token.trim().replace(/^<|>$/g, "").toLowerCase())
    .filter(Boolean);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const token of new Set(tokens)) (EMAIL.test(token) ? valid : invalid).push(token);
  return { valid, invalid };
}

/** Addresses outside the workspace's allowed domains (Settings → Security); empty list = any. */
export function outsideDomains(emails: readonly string[], allowed: readonly string[]): string[] {
  if (allowed.length === 0) return [];
  const domains = allowed.map((d) => d.trim().toLowerCase().replace(/^@/, ""));
  return emails.filter((email) => !domains.includes(email.split("@")[1] ?? ""));
}

export interface SeatState {
  used: number;
  limit: number | null;
  pending: number;
  available: number | null;
  full: boolean;
}

/** Seats from the billing meter when readable, else members vs the plan's included seats. */
export function seatState(input: { used: number; limit: number | null; pending: number }): SeatState {
  const available = input.limit === null ? null : Math.max(input.limit - input.used, 0);
  return { ...input, available, full: available !== null && available <= input.pending };
}

export function planSeatLimit(plan: string | undefined): number | null {
  return plan && plan in PLAN_LIMITS ? PLAN_LIMITS[plan as PlanKey].seats : null;
}

/** Built-in roles a member can be invited with (never Owner), in the mockup's order. */
export const INVITE_ROLE_ORDER = ["admin", "editor", "agent", "viewer"] as const;

export function invitableRoles(roles: readonly RoleOut[]): RoleOut[] {
  const builtIns = INVITE_ROLE_ORDER.map((key) => roles.find((r) => r.is_builtin && r.key === key)).filter((r): r is RoleOut => r !== undefined);
  const custom = roles.filter((r) => !r.is_builtin);
  return [...builtIns, ...custom];
}

/** Rows of the team page's role overview (tenant/team.html permission matrix). */
export const ROLE_MATRIX_ROWS = ["billing.manage", "team.manage", "knowledge.edit", "agents.edit", "inbox.reply", "analytics.view", "api_keys.manage"] as const;
export const ROLE_MATRIX_COLUMNS = ["owner", "admin", "editor", "agent", "viewer"] as const;

export function roleMatrix(roles: readonly RoleOut[]): { columns: RoleOut[]; grants: (permission: string, role: RoleOut) => boolean } {
  const columns = ROLE_MATRIX_COLUMNS.map((key) => roles.find((r) => r.is_builtin && r.key === key)).filter((r): r is RoleOut => r !== undefined);
  return { columns, grants: (permission, role) => role.permissions.includes(permission) };
}

export function parseTopics(raw: string): string[] {
  return [...new Set(raw.split(",").map((topic) => topic.trim()).filter(Boolean))];
}

/** "billing, payments go to Accounting; exams go to Admissions" from the teams' routing topics. */
export function routingRules(teams: readonly TeamOut[]): { team: string; topics: string }[] {
  return teams.filter((team) => team.routing_topics.length > 0).map((team) => ({ team: team.name, topics: team.routing_topics.join(", ") }));
}
